import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Label } from '../ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { mdiCheck, mdiClose, mdiFlaskOutline, mdiLoading } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { useApi } from '../../api/hooks'
import type { TestAggregationResponse } from '../../api/schema'
import { formatDisplayNumber } from '../../lib/format'
import { EmptyState } from '../ui/empty-state'

/** Aggregation results are averages and ratios — the arithmetic that produces
 * fifteen decimals. One decimal, like everywhere else in the product. */
function formatAggregateValue(value: unknown): string {
  return typeof value === 'number' ? formatDisplayNumber(value) : String(value)
}

interface AggregationTestPanelProps {
  org: string
  uid: string
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message?: unknown }).message === 'string') {
    return (err as { message: string }).message
  }
  return 'Unknown error'
}

export function AggregationTestPanel({ org, uid }: AggregationTestPanelProps) {
  const { t } = useTranslation('aggregations')
  const { request } = useApi()
  const [parentAssetUid, setParentAssetUid] = useState('')
  const [testResult, setTestResult] = useState<TestAggregationResponse | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  const testMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/aggregations/{aggregationUid}/test', {
    onSuccess: (data) => {
      setTestResult(data)
      setTestError(null)
    },
    onError: (err) => {
      setTestError(errorMessage(err))
      setTestResult(null)
    }
  })

  const searchAssets = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
    try {
      const queryParams: Record<string, string> = { limit: '50' }
      if (query) {
        queryParams.q = query
        queryParams.sort = 'name'
      } else {
        queryParams.sort = '-updated_at'
      }

      const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
        params: {
          path: { orgCode: org },
          query: queryParams
        }
      })

      const assets = (response as any)?.data || []
      return assets.map((asset: any) => ({
        value: asset.uid,
        label: asset.name || asset.uid,
      }))
    } catch {
      return []
    }
  }, [org, request])

  const handleRunTest = () => {
    if (!parentAssetUid) return
    testMutation.mutate({
      params: { path: { orgCode: org, aggregationUid: uid } },
      body: { parent_asset_uid: parentAssetUid },
    })
  }

  const details = testResult?.details || []
  const displayedDetails = details.slice(0, 50)
  const remainingCount = details.length - 50

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-5">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">{t('test.title')}</h3>

        <div className="flex items-end gap-4 mb-6">
          <div className="flex-1">
            <Label>{t('test.parent_asset_label')}</Label>
            <ApiSearchableSelect
              placeholder={t('test.parent_asset_placeholder')}
              onSearch={searchAssets}
              onValueChange={setParentAssetUid}
              data-testid="aggregation-test-asset-select"
            />
          </div>
          <Button
            onClick={handleRunTest}
            disabled={!parentAssetUid || testMutation.isPending}
            data-testid="aggregation-test-btn"
          >
            {testMutation.isPending ? (
              <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />
            ) : (
              <AppIcon path={mdiFlaskOutline} size="sm" className="mr-2" />
            )}
            {t('test.run_test')}
          </Button>
        </div>

        {testError && (
          <div className="text-destructive text-sm mb-4">
            {t('test.error', { error: testError })}
          </div>
        )}

        {testResult && (
          <div className="space-y-4">
            <div className="bg-accent/30 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('test.result_label')}</p>
                  <p className="text-2xl font-bold text-foreground" data-testid="aggregation-test-result">
                    {testResult.result !== null && testResult.result !== undefined ? formatAggregateValue(testResult.result) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('test.function_label')}</p>
                  <Badge variant="secondary">{testResult.function}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('test.child_count_label')}</p>
                  <p className="text-lg font-semibold" data-testid="aggregation-test-child-count">{testResult.child_count ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('test.matching_count_label')}</p>
                  <p className="text-lg font-semibold" data-testid="aggregation-test-matching-count">{testResult.matching_count ?? '-'}</p>
                </div>
              </div>
            </div>

            {displayedDetails.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">{t('test.details_title')}</h4>
                <Table data-testid="aggregation-test-details-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('test.details_name')}</TableHead>
                      <TableHead>{t('test.details_value')}</TableHead>
                      <TableHead>{t('test.details_matched')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedDetails.map((detail, idx) => (
                      <TableRow key={detail.asset_uid || idx}>
                        <TableCell>{detail.asset_name || detail.asset_uid}</TableCell>
                        <TableCell>
                          <code className="text-sm bg-muted px-1 py-0.5 rounded">
                            {detail.value !== null && detail.value !== undefined ? formatAggregateValue(detail.value) : '-'}
                          </code>
                        </TableCell>
                        <TableCell>
                          {detail.matched ? (
                            <AppIcon path={mdiCheck} size="sm" className="text-green-600" />
                          ) : (
                            <AppIcon path={mdiClose} size="sm" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {remainingCount > 0 && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {t('test.details_more', { count: remainingCount })}
                  </p>
                )}
              </div>
            )}

            {testResult.result === null && details.length === 0 && (
              <EmptyState variant="inline" className="py-4" title={t('test.no_result')} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
